import { useState } from "react";
import axiosService from "../../core/services/axios-service";
import { ApiRoutes } from "../../core/dictionary/api-url";
import {
  orgranizationDepartment,
  orgranizationRole,
} from "../../core/dictionary/orginazation-dropdown";
import ToastService from "../../core/services/toaster-service";
import "../../assets/scss/pre-team-course-survey.scss";

const YES_NO_OPTIONS = ["Yes", "No"];

const CONFIDENCE_QUESTIONS = [
  { key: "confidenceAirway", label: "Confidence opening the airway" },
  {
    key: "confidenceBreathing",
    label: "Confidence differentiating between normal and abnormal breathing",
  },
  {
    key: "confidenceCprInitiation",
    label:
      "Confidence calling for help and initiating CPR if there is no pulse or abnormal breathing",
  },
  {
    key: "confidenceCompressions",
    label:
      "Confidence performing chest compressions with correct rate, depth, recoil and minimal interruptions",
  },
  {
    key: "confidenceAedSwitch",
    label: "Confidence switching on the AED or defibrillator",
  },
  {
    key: "confidenceAedPads",
    label: "Confidence attaching the AED pads to the patient",
  },
  {
    key: "confidenceBackboard",
    label: "Confidence placing the patient on a pad or backboard",
  },
  {
    key: "confidenceAedPrompts",
    label: "Confidence following the AED voice or visual prompts",
  },
  {
    key: "confidenceShockButtons",
    label: "Confidence pressing ANALYZE and SHOCK buttons when appropriate",
  },
  {
    key: "confidenceResumeCompressions",
    label: "Confidence quickly resuming compressions immediately after shock",
  },
  {
    key: "confidenceMinimizeInterruptions",
    label: "Confidence minimizing interruptions while following the prompts",
  },
  {
    key: "confidenceContinuePrompts",
    label:
      "Confidence continuing as directed by voice or visual prompts from the AED/defibrillator",
  },
];

const initialForm = {
  fullName: "",
  email: "",
  unit: "",
  role: "",
  facility: "",
  witnessedCardiacArrest: "",
  performedCpr: "",
  usedAed: "",
  confidenceAirway: 5,
  confidenceBreathing: 5,
  confidenceCprInitiation: 5,
  confidenceCompressions: 5,
  confidenceAedSwitch: 5,
  confidenceAedPads: 5,
  confidenceBackboard: 5,
  confidenceAedPrompts: 5,
  confidenceShockButtons: 5,
  confidenceResumeCompressions: 5,
  confidenceMinimizeInterruptions: 5,
  confidenceContinuePrompts: 5,
};

export default function PreTeamCourseSurvey() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "range" ? Number(value) : value,
    }));
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Full Name is required.";
    if (!form.email.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return "Please enter a valid email address.";
    if (!form.unit) return "Unit is required.";
    if (!form.role) return "Role is required.";
    if (!form.facility.trim()) return "Facility is required.";
    if (!form.witnessedCardiacArrest)
      return "Please answer: Have you ever witnessed a cardiac arrest?";
    if (!form.performedCpr)
      return "Please answer: Have you ever performed CPR on a real person?";
    if (!form.usedAed) return "Please answer: Have you ever used an AED?";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        "Full Name": form.fullName,
        Email: form.email,
        Unit: form.unit,
        Role: form.role,
        Facility: form.facility,
        "Have you ever witnessed a cardiac arrest?": form.witnessedCardiacArrest,
        "Have you ever performed CPR on a real person?": form.performedCpr,
        "Have you ever used an AED?": form.usedAed,
        "Confidence opening the airway": form.confidenceAirway,
        "Confidence differentiating between normal and abnormal breathing":
          form.confidenceBreathing,
        "Confidence calling for help and initiating CPR if there is no pulse or abnormal breathing":
          form.confidenceCprInitiation,
        "Confidence performing chest compressions with correct rate, depth, recoil and minimal interruptions":
          form.confidenceCompressions,
        "Confidence switching on the AED or defibrillator": form.confidenceAedSwitch,
        "Confidence attaching the AED pads to the patient": form.confidenceAedPads,
        "Confidence placing the patient on a pad or backboard": form.confidenceBackboard,
        "Confidence following the AED voice or visual prompts": form.confidenceAedPrompts,
        "Confidence pressing ANALYZE and SHOCK buttons when appropriate":
          form.confidenceShockButtons,
        "Confidence quickly resuming compressions immediately after shock":
          form.confidenceResumeCompressions,
        "Confidence minimizing interruptions while following the prompts":
          form.confidenceMinimizeInterruptions,
        "Confidence continuing as directed by voice or visual prompts from the AED/defibrillator":
          form.confidenceContinuePrompts,
      };

      const res = await axiosService.post(ApiRoutes.PRE_COURSE_SURVEY, payload);
      if (res.data && res.data.success) {
        ToastService.success(res.data.message || "Survey submitted successfully!");
        setSubmitted(true);
      } else {
        const msg = res.data?.message || "Failed to submit survey. Please try again.";
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg = err?.message || "Server error. Please try again.";
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="survey-page">
      {/* Header */}
      <header className="survey-header">
        <img
          src="https://res.cloudinary.com/dhhzpdckr/image/upload/v1758128880/rescuernlogo_dsi3mf.png"
          alt="RescueRn Logo"
          className="survey-header__logo"
        />
      </header>

      {/* Main content */}
      <main className="survey-main">
        {submitted ? (
          <div className="survey-thankyou">
            <div className="survey-thankyou__icon">✓</div>
            <h2 className="survey-thankyou__title">Thank You!</h2>
            <p className="survey-thankyou__message">
              Your pre-course survey has been submitted successfully. We
              appreciate your time and look forward to seeing you in the
              training.
            </p>
          </div>
        ) : (
          <div className="survey-card">
            <div className="survey-card__intro">
              <h1 className="survey-card__title">Pre-Course Survey</h1>
              <p className="survey-card__subtitle">
                Please complete this short survey before your resuscitation
                training session.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Section 1: Personal Information */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Personal Information</h3>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="fullName">
                      Full Name <span className="survey-required">*</span>
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      name="fullName"
                      className="survey-input"
                      placeholder="Enter your full name"
                      value={form.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="email">
                      Email <span className="survey-required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="survey-input"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="survey-row">
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="unit">
                      Unit <span className="survey-required">*</span>
                    </label>
                    <select
                      id="unit"
                      name="unit"
                      className="survey-select"
                      value={form.unit}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Unit</option>
                      {orgranizationDepartment.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="survey-field">
                    <label className="survey-label" htmlFor="role">
                      Role <span className="survey-required">*</span>
                    </label>
                    <select
                      id="role"
                      name="role"
                      className="survey-select"
                      value={form.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Role</option>
                      {orgranizationRole.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="survey-field survey-field--full">
                  <label className="survey-label" htmlFor="facility">
                    Facility <span className="survey-required">*</span>
                  </label>
                  <input
                    id="facility"
                    type="text"
                    name="facility"
                    className="survey-input"
                    placeholder="Enter your facility name"
                    value={form.facility}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Section 2: Prior Experience */}
              <div className="survey-section">
                <h3 className="survey-section__heading">Prior Experience</h3>

                {[
                  {
                    name: "witnessedCardiacArrest",
                    label: "Have you ever witnessed a cardiac arrest?",
                  },
                  {
                    name: "performedCpr",
                    label: "Have you ever performed CPR on a real person?",
                  },
                  {
                    name: "usedAed",
                    label: "Have you ever used an AED?",
                  },
                ].map((q) => (
                  <div key={q.name} className="survey-field survey-field--full">
                    <label className="survey-label">
                      {q.label} <span className="survey-required">*</span>
                    </label>
                    <div className="survey-radio-group">
                      {YES_NO_OPTIONS.map((opt) => (
                        <label key={opt} className="survey-radio-label">
                          <input
                            type="radio"
                            name={q.name}
                            value={opt}
                            checked={form[q.name] === opt}
                            onChange={handleChange}
                            className="survey-radio"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Section 3: Confidence Assessment */}
              <div className="survey-section">
                <h3 className="survey-section__heading">
                  Confidence Assessment
                </h3>
                <p className="survey-section__description">
                  Rate your confidence level from <strong>1</strong> (not
                  confident) to <strong>10</strong> (very confident).
                </p>

                {CONFIDENCE_QUESTIONS.map((q) => (
                  <div key={q.key} className="survey-field survey-field--full survey-slider-field">
                    <label className="survey-label survey-slider-label">
                      {q.label}
                    </label>
                    <div className="survey-slider-row">
                      <span className="survey-slider-edge">1</span>
                      <input
                        type="range"
                        name={q.key}
                        min="1"
                        max="10"
                        step="1"
                        value={form[q.key]}
                        onChange={handleChange}
                        className="survey-slider"
                      />
                      <span className="survey-slider-edge">10</span>
                      <span className="survey-slider-value">{form[q.key]}</span>
                    </div>
                  </div>
                ))}
              </div>

              {error && <div className="survey-error">{error}</div>}

              <button
                type="submit"
                className="survey-submit-btn"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Survey"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
